import React, { Component } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';

import { throttle } from 'lodash';
import { SHAREDB_COLLECTION } from '../config';
import { withShareDB } from '../providers/ShareDBProvider';
import { addUserInfo } from '../resources/ops';
import SharedbDocTimeline from './ShareDBDocTimeline';
import { EXPLORE_PATHS, CONNECTION_STATUS } from '../constants';
import { heartbeatKernel } from '../resources/api';

// After this threshold the connection is considered delayed and an
// error banner is shown to the user.
const SHAREDB_DELAY_THRESHOLD_MS = 5000;
const KERNEL_HEARTBEAT_MAX_EVERY_MS = 60 * 1000;

const throttledKernelHeartbeat = throttle(
  (docId) => {
    heartbeatKernel(docId);
  },
  KERNEL_HEARTBEAT_MAX_EVERY_MS,
  { leading: true }
);

class SharedbDocNormal extends Component {
  constructor(props) {
    super(props);
    this.doc = null;
    this.state = {
      data: {},
      presence: {},
      connectionStatus: '',
    };
  }

  // either document is flagged as read only, or this session is read only
  // NOTE: Keep this in sync with collaboration/server/sharedbWebsocket.js
  isReadOnly = () => {
    const { data } = this.state;
    const { readOnly, currentUser } = this.props;

    // If the `isInstructor` flag is set in addition to either `preview` or it has `pins` then always enable editing.
    // This condition can occur when the instructor is viewing rollup documents. In this rollup viewing condition,
    // the instructor will be unable to change the selections in the document if the document was set to read only.
    // Note: even in this rollup condition the instructor cannot edit the students' responses.
    if (currentUser.isInstructor && (data.preview || data.pins)) {
      return false;
    }
    return data.readOnly || readOnly || data.deprecated;
  };

  subscribe = () => {
    const { sharedb, docId, currentUser, noPresence } = this.props;
    this.doc = sharedb.get(SHAREDB_COLLECTION, docId);

    // This is because each cell in the table independently
    // listens to the ShareDB doc. TODO: Have a central table
    // listener which dispatches changes only to the cell
    // which is affected.
    this.doc.setMaxListeners(150);
    this.doc.requestReplyPresence = true;
    this.doc._submitOp = this.doc.submitOp;
    this.doc.submitOp = (op, options, callback) => {
      // `op` can be an array or an object depending on what calls submitOp.
      let path;
      if (Array.isArray(op) && op.length > 0) {
        path = op[0].p;
      } else {
        path = op.p;
      }
      const modifyingMainDocument =
        path && !Object.values(EXPLORE_PATHS).includes(path[0]);
      if (!(this.isReadOnly() && modifyingMainDocument)) {
        const timer = setTimeout(() => {
          this.setState({
            connectionStatus: CONNECTION_STATUS.delayed,
          });
        }, SHAREDB_DELAY_THRESHOLD_MS);
        this.doc._submitOp(op, options, (error) => {
          clearTimeout(timer);
          if (error) {
            this.setState({
              connectionStatus: CONNECTION_STATUS.error,
            });
            console.error('SubmitOp error', error);
          } else {
            this.setState({
              connectionStatus: CONNECTION_STATUS.ok,
            });
          }
          if (callback) {
            callback(error);
          }
        });
      }

      // Only in worksheets that have an initialized kernel, we make sure that the kernel
      // stays alive while the user is actively working on the worksheet.
      if (this.doc?.data?.kernelState) {
        throttledKernelHeartbeat(docId);
      }
    };

    this.doc.subscribe((err) => {
      if (err) {
        throw err;
      }
      if (this.doc.type === null) {
        throw new Error(`Worksheet ${docId} not found.`);
      }

      console.log('[ShareDBDoc] Document subscribed', {
        docId,
        version: this.doc.version,
        kernelState: this.doc.data?.kernelState,
        runningCell: this.doc.data?.runningCell,
        timestamp: new Date().toISOString(),
      });

      this.setState({
        data: cloneDeep(this.doc.data),
      });

      // we need to send a presence to receive other users' presence, but if we don't want
      // to share our presence, we set our userId to undefined, which stops other clients
      // from rendering it
      this.doc.submitPresence(
        {
          u: noPresence ? undefined : currentUser.userId,
        },
        () => {
          this.doc.requestReplyPresence = false;
        }
      );

      if (!noPresence && !this.doc.data.users[currentUser.userId]) {
        this.doc.submitOp(addUserInfo(currentUser));
      }
    });

    this.doc.on('op', (ops, source) => {
      // Log ops for debugging stuck kernel state
      const kernelOps = ops.filter((op) => op.p && (op.p[0] === 'kernelState' || op.p[0] === 'runningCell'));
      if (kernelOps.length > 0) {
        console.log('[ShareDBDoc] Received kernel-related ops', {
          docId,
          ops: kernelOps,
          source,
          version: this.doc.version,
          kernelState: this.doc.data?.kernelState,
          runningCell: this.doc.data?.runningCell,
          timestamp: new Date().toISOString(),
        });
      }
      this.onOp();
    });
    this.doc.on('del', this.onDel);
    this.doc.on('error', this.onError);
    this.doc.on('presence', () =>
      this.setState({ presence: this.doc.presence })
    );
  };

  onOp = () => {
    this.setState({
      data: cloneDeep(this.doc.data),
    });
  };

  onDel = () => {
    const { docId } = this.props;
    console.error(`doc ${docId} got deleted.`);
    this.unsubscribe();
  };

  onError = (err) => {
    console.error(err);
  };

  unsubscribe = () => {
    if (this.doc) {
      this.doc.destroy();
      this.doc = null;
    }
  };

  componentDidMount() {
    this.subscribe();
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  componentDidUpdate(prevProps) {
    const { docId } = this.props;
    if (prevProps.docId !== docId) {
      this.unsubscribe();
      this.subscribe();
    }
  }

  render() {
    const { data, presence, connectionStatus } = this.state;
    const { children } = this.props;
    return isEmpty(data) || this.doc === null ? (
      <div
        className="flex align-center justify-center pt4"
        data-cy="loading-worksheet"
      >
        Loading...
      </div>
    ) : (
      children({
        doc: this.doc,
        data,
        presence,
        readOnlyWorksheet: this.isReadOnly(),
        connectionStatus,
      })
    );
  }
}

SharedbDocNormal.propTypes = {
  docId: PropTypes.string.isRequired,
  sharedb: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  children: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  noPresence: PropTypes.bool.isRequired,
  templateDataFromAuthoring: PropTypes.object,
};

const SharedbDoc = ({ currentUser, ...rest }) => {
  if (currentUser.timeline) {
    return <SharedbDocTimeline currentUser={currentUser} {...rest} />;
  }
  return <SharedbDocNormal currentUser={currentUser} {...rest} />;
};

export default withShareDB()(SharedbDoc);
